import React, { useEffect, useState, useRef } from "react"
import dynamic from "next/dynamic"
import { useSelector, useDispatch } from "react-redux"
import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import SlideDrawer from "@/components/core/SlideDrawer"
import htmlParser from "html-react-parser"
import ToolTip from "@/components/core/ToolTip/ToolTip"

import {
  locationState,
  setUtilityLocation,
} from "@/store/features/locationSlice"
import { getUtilityStoreDetails } from "@/utils/location"
import {
  addAnalyticsForBuymore,
  addAnalyticsForAddService,
} from "@/components/ProductDetail/v3/ProductDetailView/analytics/analytics"
import {
  servicesClick,
  titleclickhandler,
  installserviceClickHandler,
  getProsessionalClickHandler,
  handleAddToCart,
  getExcludedZipcode,
  normalizeZipCode,
} from "@/components/ProductDetail/v3/ProductDetailView/ProductOnDisplay/utils"
import { createContextLogger } from "@/utils/logger/client"

const DisplayItem = dynamic(() =>
  import(
    "@/components/ProductDetail/v3/ProductDetailView/ProductOnDisplay/DisplayItem"
  )
)

import _isEqual from "lodash/isEqual"

const ProductOnDisplay = props => {
  const {
    pdpJson = {},
    productServices = {},
    installationData = {},
    currencyCode = "",
    currencySign = "$",
    brandName = "",
    findStores = "",
    colorsData = {},
    shortendPagePath = "",
    findStoreDetailsUrl = "",
    productType = "",
    productInfo = {},
    getZipCode = () => {},
    productOnDisplayData = {},
    isRetailProduct = false,
    isParts = false,
    isProductDiscontinued = false,
    zipCodeData = [],
  } = props || {}

  const {
    locationTitles = "",
    zipCodeToUpdate = "",
    updateZipTitle = "",
    productDisplayTitle = "",
    addInstallServiceTitle = "",
    changeZipCode = "",
    yourZipCode = "",
    serviceUnavailableTitle = "",
    serviceUnavailableSectionHeading = "",
    descriptionProductNotAvailable = "",
    errorMessage = "",
    multiDesignServices = [],
    installServices = [],
    zipCodeTitle = "",
    buyMoreTitle = "",
    buyMoreToolTip = "",
    addServiceToCart = "",
    productOnDisplay = {},
    sectionHeadingProductNotAvailable,
  } = pdpJson

  const logger = createContextLogger("pdp:product-on-display")
  const { findTitle = "", findDesc = "" } = productOnDisplay

  const { utilityLocation = "" } = useSelector(locationState)
  const normalizedUtilityLocation = normalizeZipCode(utilityLocation)

  const [tooltip, setTooltip] = useState({ buyMoreSaveMore: false })
  const [statusMessage, setStatusMessage] = useState("")
  const [serviceDetails, setServiceDetails] = useState({})
  const [servicePrice, setServicePrice] = useState("")
  const [zipCodeInputVal, setZipCodeInputVal] = useState(
    normalizedUtilityLocation
  )
  const [showAddanInstallCard, setShowAddanInstallCard] = useState(false)
  const [toggleZipCodeSection, setToggleZipCodeSection] = useState(
    zipCodeInputVal ? true : false
  )
  const [colorsDataState, setColorsDataState] = useState(colorsData)
  const [colorsDatahasChanged, setColorsDatahasChanged] = useState(false)
  const [findStoreUtilityDetails, setFindStoreUtilityDetails] = useState({})
  const [isSliderOpen, setIsSliderOpen] = useState(false)
  const [serviceSku, setServiceSku] = useState("")
  const [isZipCodeExcluded, setIsZipCodeExcluded] = useState(false)

  const learnMoreRef = useRef()
  const installSeriveRef = useRef()
  const isInitionalRender = useRef(true)
  const storeNearTriggerRef = useRef(null)
  const zipInputRef = useRef(null)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!_isEqual(colorsData, colorsDataState)) {
      setColorsDataState(colorsData)
    }
  }, [colorsData])

  useEffect(() => {
    if (showAddanInstallCard > 0) {
      if (installSeriveRef?.current) {
        installSeriveRef?.current?.querySelectorAll("a").forEach(item => {
          if (item?.getAttribute("listener") !== "true") {
            item?.addEventListener("click", e => {
              servicesClick(e, productInfo)
            })
          }
        })
      }
    }
    return () => {
      installSeriveRef?.current?.querySelectorAll("a").forEach(item => {
        item?.removeEventListener("click", servicesClick)
      })
    }
  }, [showAddanInstallCard, servicePrice])

  useEffect(() => {
    if (utilityLocation) setZipCodeInputVal(normalizedUtilityLocation)
  }, [utilityLocation])

  useEffect(() => {
    isInitionalRender.current = true
    const storedZipCode = normalizeZipCode(
      localStorage?.getItem("currentZipCode")
    )
    if (!zipCodeInputVal) {
      setZipCodeInputVal(storedZipCode)
    }
    handleCheckAvailability(storedZipCode, true)

    fetchFindStoreUtilityDetails()
    if (learnMoreRef?.current) {
      learnMoreRef?.current?.querySelectorAll("a").forEach(item => {
        item?.addEventListener("click", e => {
          installserviceClickHandler(e, productInfo)
        })
      })
    }
    return () => {
      learnMoreRef?.current?.querySelectorAll("a").forEach(item => {
        item?.removeEventListener("click", e => {
          installserviceClickHandler(e, productInfo)
        })
      })
    }
  }, [])

  useEffect(() => {
    if (!toggleZipCodeSection) {
      zipInputRef.current?.focus({ preventScroll: true })
    }
  }, [toggleZipCodeSection])

  const fetchFindStoreUtilityDetails = () => {
    getUtilityStoreDetails({ utilityStoreDetailsUrl: findStoreDetailsUrl })
      .then(response => {
        setFindStoreUtilityDetails(response)
      })
      .catch(error => {
        logger.error("Failed to fetch find store utility details", {
          error,
          findStoreDetailsUrl,
        })
      })
  }

  useEffect(() => {
    if (productServices.length) {
      const qualifiedService =
        installServices.find(item => item.sku === productServices[0]) ?? {}
      setServiceDetails(qualifiedService)
      if (installationData.length) {
        const qaulifiedServiceBusinessData =
          installationData.find(
            item => item.masterVariant.sku === productServices[0]
          ) ?? {}

        const { masterVariant: { prices = [], sku = "" } = {} } =
          qaulifiedServiceBusinessData ?? {}

        setServiceSku(sku)

        if (prices.length) {
          const priceObj =
            prices.find(item => item.value.currencyCode === currencyCode) ?? {}
          const { value: { centAmount = "" } = {} } = priceObj
          const installationPrice = Number(centAmount / 100).toFixed(2)
          setServicePrice(installationPrice)
        }
      }
    }
  }, [productServices, installationData])

  useEffect(() => {
    if (!colorsDatahasChanged) setColorsDatahasChanged(true)
  }, [colorsDataState])

  const handleFieldInfoMouseOver = (
    tooltipFor = "buyMoreSaveMore",
    value = false
  ) => {
    setTooltip({ ...tooltip, [tooltipFor]: value })
  }

  useEffect(() => {
    if (
      normalizedUtilityLocation?.length &&
      serviceSku &&
      !showAddanInstallCard
    ) {
      const zipCodeExcluded = getExcludedZipcode({
        zipCodeData,
        zipCodeInputVal,
        serviceSku,
      })
      setIsZipCodeExcluded(zipCodeExcluded)
    }
  }, [normalizedUtilityLocation, serviceSku, zipCodeData])

  const handlerForAddServiceClick = e => {
    e.stopPropagation()
    handleAddToCart({ e, productServices, zipCodeInputVal, brandName, logger })
    addAnalyticsForAddService(
      addServiceToCart,
      productInfo,
      serviceDetails.sectionHeading,
      servicePrice,
      productServices[0]
    )
  }

  const handleChangeZip = () => {
    setToggleZipCodeSection(false)
    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      eventMetaData: {
        experience: "pdp",
        eventType: "addAnalyticsForChangeZipCode",
        params: {
          titleName: changeZipCode,
          zipval: zipCodeInputVal,
        },
      },
    })
  }

  const handleZipCodeCallBack = isZipAvaialbe => {
    setShowAddanInstallCard(isZipAvaialbe)
    setToggleZipCodeSection(true)
    if (!isInitionalRender.current) {
      const kohlerDatalayer = window.kohler.datalayer
      kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
        eventMetaData: {
          experience: "pdp",
          eventType: "addAnalyticsForUpdate",
          params: {
            titleName: updateZipTitle,
            zipval: zipCodeInputVal,
            showcard: isZipAvaialbe,
          },
        },
      })
    }
  }

  const handleCheckAvailability = (zipcode, isInitialCall = false) => {
    const zipToPass = normalizeZipCode(zipCodeInputVal || zipcode)
    getZipCode(zipToPass, handleZipCodeCallBack)
  }

  const handleUpadteClick = () => {
    dispatch(setUtilityLocation({ utilityLocation: zipCodeInputVal }))
    isInitionalRender.current = false
    handleCheckAvailability()
    setStatusMessage("Zipcode updated successfully")
    setTimeout(() => setStatusMessage(""), 3000)
  }

  const dispalyData = {
    productOnDisplay,
    colorsData,
    findStores,
    brandName,
    findStoreUtilityDetails,
    shortendPagePath,
    productOnDisplayData,
  }

  const isShowProductOnDisplay =
    !isProductDiscontinued && !isRetailProduct && !isParts

  const storeNearClick = () => {
    setIsSliderOpen(true)
    const kohlerDatalayer = window.kohler.datalayer
    kohlerDatalayer.publish(kohlerDatalayer.EventTypes.CLICK, {
      eventMetaData: {
        experience: "pdp",
        eventType: "addAnalyticsClickStoreNear",
        params: {
          productDisplayTitle: productDisplayTitle.toLowerCase(),
          sectionHeadingProductNotAvailable:
            sectionHeadingProductNotAvailable.toLowerCase(),
        },
      },
    })
  }

  const handleDrawerClose = () => {
    setIsSliderOpen(false)
    setTimeout(() => {
      if (storeNearTriggerRef.current) {
        storeNearTriggerRef.current.focus()
      }
    }, 0)
  }

  return (
    <>
      <div
        aria-live="polite"
        role="status"
        aria-atomic="true"
        className="zip-update-status"
      >
        {statusMessage}
      </div>
      <div>
        <div className="title-product-on-display">{locationTitles}</div>
        {zipCodeInputVal && toggleZipCodeSection ? (
          <div className="current-location">
            <div className="location">{`${yourZipCode}: ${zipCodeInputVal}`}</div>
            <p
              tabIndex="0"
              role="button"
              className="chnage-location"
              onClick={handleChangeZip}
            >
              {changeZipCode}
            </p>
          </div>
        ) : (
          <div className="change-zip">
            <div className="zip-changes">{zipCodeToUpdate}</div>
            <Input
              id="zip-code"
              type="text"
              value={zipCodeInputVal}
              maxLength={5}
              placeholder={zipCodeTitle}
              label={zipCodeTitle}
              onKeyDown={e => e.key === "Enter" && handleCheckAvailability()}
              onChange={e => setZipCodeInputVal(e.target.value)}
              ref={zipInputRef}
            />
            <p
              tabIndex="0"
              role="button"
              className="update"
              onClick={handleUpadteClick}
            >
              {updateZipTitle}
            </p>
          </div>
        )}
        {isShowProductOnDisplay ? (
          <div className="productNotOnDisplay">
            <div className="left-side">{productDisplayTitle}</div>
            <div className="right-side">
              <button
                className="right-side__title"
                ref={storeNearTriggerRef}
                type="button"
                onClick={storeNearClick}
                aria-label={sectionHeadingProductNotAvailable}
              >
                {sectionHeadingProductNotAvailable}
              </button>
              <div className="notdisplay-desc">
                {descriptionProductNotAvailable}
              </div>
            </div>
          </div>
        ) : null}

        {productServices.length &&
        servicePrice > 0 &&
        normalizedUtilityLocation.length === 5 &&
        (showAddanInstallCard || !isZipCodeExcluded) ? (
          <div className="add-an-install-service">
            <div className="add-an-install-service__left-side">
              {addInstallServiceTitle}
            </div>
            <div className="add-an-install-service__right-side">
              <div
                className="title"
                role="link"
                onClick={e => {
                  e.stopPropagation()
                  titleclickhandler(e, productInfo)
                }}
              >
                {serviceDetails.sectionHeading
                  ? htmlParser(serviceDetails.sectionHeading)
                  : null}
              </div>
              <div
                className="install-service"
                ref={installSeriveRef}
                onClick={e => e.stopPropagation()}
              >
                {serviceDetails?.sectiondescription
                  ? htmlParser(serviceDetails.sectiondescription)
                  : null}
              </div>
              <div
                className="tool-tip"
                onMouseOver={() => {
                  handleFieldInfoMouseOver("buyMoreSaveMore", true)
                  addAnalyticsForBuymore(buyMoreTitle, "", productInfo)
                }}
                onFocus={() => {
                  handleFieldInfoMouseOver("buyMoreSaveMore", true)
                }}
                onMouseLeave={() =>
                  handleFieldInfoMouseOver("buyMoreSaveMore", false)
                }
                onBlur={() =>
                  handleFieldInfoMouseOver("buyMoreSaveMore", false)
                }
              >
                <div
                  className={`tool-tip ${
                    tooltip.buyMoreSaveMore ? "--show" : "--hide"
                  }`}
                >
                  <div className="tool-tip-wrapper">
                    <ToolTip message={buyMoreToolTip}></ToolTip>
                  </div>
                </div>
                {buyMoreTitle}
              </div>
              <div className="price-button">
                <div className="price">{`${currencySign} ${servicePrice}`}</div>
                <Button
                  type="primary"
                  size="small"
                  label={addServiceToCart}
                  onClick={e => {
                    handlerForAddServiceClick(e)
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="add-installation-noservice">
            <div className="noservice-title">{serviceUnavailableTitle}</div>
            <div className="noservice__right-side">
              <div
                className="header"
                role="link"
                onClick={e => {
                  getProsessionalClickHandler(e, productInfo)
                }}
              >
                {htmlParser(serviceUnavailableSectionHeading)}
              </div>
              <div className="desc">{htmlParser(errorMessage)}</div>
            </div>
          </div>
        )}
        {multiDesignServices.map((item, index) => {
          const productTypeIndex = item.productType.findIndex(ele =>
            ele.includes(productType)
          )
          if (productTypeIndex > -1) {
            return (
              <div key={index} className="purchase-design" ref={learnMoreRef}>
                <div className="purchase-design__left-side">
                  {item.designPlanTitle
                    ? htmlParser(item.designPlanTitle)
                    : null}
                </div>
                <div className="purchase-design__right-side">
                  <div className="design-service">
                    {item.multiSectionHeadingDesign
                      ? htmlParser(item.multiSectionHeadingDesign)
                      : null}
                  </div>
                  <div className="expert-design">
                    {item.multiserviceDescription
                      ? htmlParser(item.multiserviceDescription)
                      : null}
                  </div>
                </div>
              </div>
            )
          }
        })}
      </div>
      <SlideDrawer
        title={findTitle}
        subTitle={findDesc}
        isOpen={isSliderOpen}
        onClose={handleDrawerClose}
      >
        {isSliderOpen ? <DisplayItem data={dispalyData} /> : null}
      </SlideDrawer>
    </>
  )
}
export default ProductOnDisplay
